import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { type AppError } from "@/shared/api";

import * as sessionApi from "../api/sessionApi";
import { clearHint, readHint, writeHint } from "./sessionHint";
import type { Company, LoginCredentials, SessionStatus } from "./types";

/**
 * Session-стор (ADR-0006 D1) — єдине джерело істини про автентифікованість для
 * guard (`app/`), header (`widgets/AppLayout`), login (`features/auth/login`).
 *
 * Amendment A1 (bug-229): гідратується оптимістично з `sessionStorage`-хінта
 * (D6′) до mount; реальна сесія реvalid-ується у фоні. In-memory тільки для
 * чутливих даних (токен, CSRF) — ADR-0006 D6.
 *
 * Δ Vuex: setup-store — `ref`/`computed`/функції; mutations немає, action мутує
 * `ref` напряму; у компоненті `const session = useSessionStore()`.
 */
export const useSessionStore = defineStore("session", () => {
  const company = ref<Company | null>(null);
  const status = ref<SessionStatus>("idle");

  // Single-flight promise для cold-path bootstrap (D5′).
  // Не реактивний ref — внутрішній деталь реалізації, не потребує спостереження.
  let bootstrapPromise: Promise<void> | null = null;

  /** Похідний від наявності company (ADR-0006 D1) — не дублююче boolean-поле. */
  const isAuthenticated = computed(() => company.value !== null);

  /**
   * Bootstrap завершено (ADR-0006 D1, amended A1): не `idle` і не `pending`.
   * Після Amendment A1 повертає `true` і для оптимістичної ('authenticated'
   * з hint) — guard вирішує синхронно, без очікування підтвердження сервера.
   * Не означає «підтверджено сервером» — лише «є рішення для guard».
   */
  const isBootstrapped = computed(
    () => status.value !== "idle" && status.value !== "pending",
  );
  const companyName = computed(() => company.value?.name ?? "");

  /**
   * Оптимістична гідратація (ADR-0006 D2′, A1): синхронна, до mount.
   * Читає hint з sessionStorage; якщо є — company + status=authenticated.
   * Немає — no-op (status лишається idle, cold-path).
   */
  function hydrateOptimistic(): void {
    const hint = readHint();
    if (!hint) return;
    company.value = hint;
    status.value = "authenticated";
  }

  /**
   * `POST /api/auth/login` → `fetchCompany` → set company (ADR-0006 D2). На
   * помилку — кидаємо далі, стан стору НЕ міняється (login-форма показує помилку
   * локально); company/status лишаються попередніми (spec §4.4).
   */
  async function login(creds: LoginCredentials): Promise<void> {
    await sessionApi.login(creds);
    const c = await sessionApi.fetchCompany();
    company.value = c;
    status.value = "authenticated";
    writeHint(c);
  }

  /** Підтягнути company поточної сесії (header-sync після save salon-profile, D6′). */
  async function fetchCompany(): Promise<void> {
    const c = await sessionApi.fetchCompany();
    company.value = c;
    status.value = "authenticated";
    writeHint(c);
  }

  /**
   * Eager silent-hydrate на старті SPA — cold path (ADR-0006 D2, amended A1 D2′):
   * викликається коли hint відсутній (`whenBootstrapped()`), або напряму з
   * `onBootRetry` (App.vue). Несе `skipAuthRedirect`:
   * - 200 → `authenticated` + company + writeHint;
   * - `kind==='auth'` (401) → `reset()` (anonymous);
   * - решта → `error` (boot-error екран із retry).
   */
  async function bootstrap(): Promise<void> {
    status.value = "pending";
    try {
      const c = await sessionApi.fetchCompany({ skipAuthRedirect: true });
      company.value = c;
      status.value = "authenticated";
      writeHint(c);
    } catch (e) {
      if ((e as AppError).kind === "auth") {
        reset();
      } else {
        status.value = "error";
      }
    }
  }

  /**
   * Фонова реvalidація (ADR-0006 D2′, hint-path): перевіряє реальну сесію
   * БЕЗ `skipAuthRedirect` — 401 проходить через D4-seam:
   * - 200 → reconcile company + writeHint, stay authenticated;
   * - 401 → D4-seam вже викликав reset() + redirect (не втручаємось);
   * - network/5xx → ЗБЕРІГАЄМО оптимістичний стан, ковтаємо (лог уже зробив interceptor).
   */
  async function revalidate(): Promise<void> {
    try {
      const c = await sessionApi.fetchCompany();
      company.value = c;
      writeHint(c);
      status.value = "authenticated";
    } catch {
      // network/5xx: не ламаємо робочий UI — зберігаємо оптимістичний стан.
      // Логування вже зроблено response-interceptором (logHttpError, ADR-0011 D4).
      // auth (401): D4-seam вже скинув стан і редіректнув — нічого не робимо.
    }
  }

  /**
   * Single-flight обгортка над `bootstrap()` (ADR-0006 D5′): guard у router
   * `await session.whenBootstrapped()` — отримує той самий in-flight promise,
   * не дублює запит. main.ts теж викликає в hint-absent path.
   */
  function whenBootstrapped(): Promise<void> {
    if (isBootstrapped.value) return Promise.resolve();
    if (bootstrapPromise !== null) return bootstrapPromise;
    // Скидаємо після settle — single-flight на час in-flight;
    // після resolve/reject наступний caller (onBootRetry) отримає свіжий promise.
    bootstrapPromise = bootstrap().finally(() => {
      bootstrapPromise = null;
    });
    return bootstrapPromise;
  }

  /**
   * Logout (spec §9.2): `POST /api/auth/logout` → `reset()`. **Idempotent** —
   * 204 і будь-яку помилку трактуємо однаково: стан ЗАВЖДИ чистимо у `finally`.
   */
  async function logout(): Promise<void> {
    try {
      await sessionApi.logout();
    } catch {
      // ignore: logout idempotent
    } finally {
      reset();
    }
  }

  /** Скинути сесію (logout / 401-handler) — company=null, status=anonymous, hint cleared. */
  function reset(): void {
    company.value = null;
    status.value = "anonymous";
    clearHint();
  }

  return {
    company,
    status,
    isAuthenticated,
    isBootstrapped,
    companyName,
    hydrateOptimistic,
    login,
    fetchCompany,
    bootstrap,
    revalidate,
    whenBootstrapped,
    logout,
    reset,
  };
});
